import { EventEmitter, Injectable } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { AuthSelectors, AuthState } from "@app/auth/store";
import { RouterGo, routerGo } from "@app/root-store/actions";
import { Store, select } from "@ngrx/store";
import { Observable, combineLatest, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { MatDialog } from "@angular/material/dialog";
import { NavigationExtras } from "@angular/router";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuthUser } from "@app/auth/model/auth-user";
import { entityRoutePaths } from "@app/entity/entity.routes";
import { EntitySelectors } from "@app/entity/store";
import { RouterActions } from "@app/root-store";
import { LoadingState } from "@app/root-store/api-result.state";
import { RouterDispatchers } from "@app/root-store/dispatchers";
import { ClareDialogComponent } from "@app/shared/components/clare-dialog/clare-dialog.component";
import { CommonModelService } from "@app/shared/services/common-model.service";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { ErrorToSuppress } from "@app/shared/services/db-crud-helper";
import { User, UserHelper } from "../model/user";
import { UserManagementSelectors } from "../store";
import { UserActions } from "../store/actions";
import { UserManagementState } from "../store/user-management.state";
import { UserDbService } from "./user-db.service";

@Injectable({
    providedIn: "root",
})
export class UserManagementService extends CommonModelService {
    /* Methods in CommonModelService:
     *  dispatchAndSelectUserRoles
     *  dispatchUserRoles
     *  selectAuthEntityIdAndDispatchEntityUsers
     */

    private readonly PERMISSION_DENIED_ERROR = ErrorToSuppress.PERMISSION_DENIED_ERROR;

    generateUserInviteCode() {
        return Math.random().toString(36).substr(2, 9); // NOSONAR
    }

    getUserByEmailExactMatch(email: string) {
        return this.store.pipe(select(UserManagementSelectors.getUserByEmailExactMatch(email)));
    }

    getUpsertAction(
        user: User,
        callbackEmitter: EventEmitter<UserActions.UserActions | RouterGo>,
        logData: any = {}
    ): UserActions.UserAction {
        if (!user.inviteCode) {
            user.inviteCode = this.generateUserInviteCode();
        }
        if (user.id) {
            return UserActions.update({ user, callbackEmitter, logData });
        }

        return UserActions.create({ user, callbackEmitter, logData });
    }

    getUpsertFailureAction(error: DataServiceError<User>): UserActions.UserFailureAction {
        if (error.requestData.id) {
            return UserActions.updateFailure({ error });
        }

        return UserActions.createFailure({ error });
    }

    getUser(userId: number) {
        return this.store.pipe(select(UserManagementSelectors.getUser(userId)));
    }

    getCallState() {
        return this.store.pipe(select(UserManagementSelectors.getCallState));
    }

    getCurrentEntityId$() {
        return this.store.pipe(select(EntitySelectors.getCurrentEntityId));
    }

    getEntityStaffList$(entityId: number = null) {
        return combineLatest({
            state: this.store.pipe(select(UserManagementSelectors.getEntityStaffState)),
            activeEntityId: entityId ? of(entityId) : this.getCurrentEntityId$(),
            userRolesCallState: this.store.pipe(select(UserManagementSelectors.getUserRolesCallState)),
        }).pipe(
            filter(({ activeEntityId, userRolesCallState }) => {
                if (userRolesCallState === LoadingState.init) {
                    this.dispatchUserRoles();
                }

                return !!activeEntityId;
            }),
            filter(({ state, activeEntityId }) => {
                if (state.entityId !== activeEntityId || state.callState === LoadingState.init) {
                    this.store.dispatch(UserActions.loadEntityUsers({ entityId: activeEntityId }));
                }

                return state.entityId === activeEntityId && state.callState === LoadingState.loaded;
            }),
            switchMap(() => this.store.pipe(select(UserManagementSelectors.getEntityStaffList)))
        );
    }

    selectEntityStaff$(isMobileView = false) {
        return this.store.pipe(
            select(!isMobileView ? UserManagementSelectors.getEntityStaffList : UserManagementSelectors.getMobileEntityStaffList)
        );
    }

    getEntityStaffUsersCallState$(entityId: number) {
        return this.store.pipe(
            select(UserManagementSelectors.getEntityStaffState),
            map(state => (state.entityId === entityId ? state.callState : LoadingState.init))
        );
    }

    getUserList(entityId: number) {
        return this.store.pipe(select(UserManagementSelectors.getUserList(entityId)));
    }

    getAllEntityUser() {
        return this.store.pipe(select(UserManagementSelectors.getCallState)).pipe(
            withLatestFrom(this.selectAuthUser()),
            filter(([callState, authUser]) => {
                if (!authUser) {
                    return false;
                }
                if (callState === LoadingState.init) {
                    this.store.dispatch(UserActions.loadAllEntityUsers({ entityId: authUser?.entityId }));
                }

                return callState === LoadingState.loaded;
            })
        );
    }

    getAllUserList() {
        return this.getAllEntityUser().pipe(
            switchMap(_ =>
                combineLatest([
                    this.store.pipe(select(UserManagementSelectors.getAllUserList)),
                    this.store.pipe(select(UserManagementSelectors.getCustomerRoleId)),
                    this.store.pipe(select(UserManagementSelectors.getUserRolesCallState)),
                    this.store.pipe(select(EntitySelectors.selectEntities)),
                ])
            ),
            map(([data]) => data)
        );
    }

    getMobileUserList(entityId: number) {
        return this.store.pipe(select(UserManagementSelectors.getMobileUserList(entityId)));
    }

    getCurrentUser() {
        return this.store.pipe(select(UserManagementSelectors.getCurrentUser));
    }

    /**
     * Get entity staff by Id
     *
     * @param {number} staffId id of staff
     * @returns {Observale<User>}
     */
    getStaffById(staffId: number): Observable<User> {
        return this.store.pipe(select(UserManagementSelectors.getSelectedStaff(staffId)));
    }

    getAuthUser(): Observable<AuthUser> {
        return this.store.pipe(
            select(AuthSelectors.selectGetUser),
            filter(authUser => !!authUser)
        );
    }

    upsertUser(
        form: FormGroup,
        callbackEmitter: EventEmitter<UserActions.UserActions | RouterGo>,
        user: User = {} as User,
        logData: any = {}
    ) {
        let action: UserActions.UserActions;

        if (!form.invalid) {
            const rawValue = form.getRawValue();
            const roleId = +rawValue.roleId || null;
            // get all fields for the user, but don't override the email if the user already has a gitkitId
            const addlProps = user.gitkitId ? { email: user.email } : {};
            action = this.getUpsertAction({ ...user, ...rawValue, ...addlProps, roleId }, callbackEmitter, logData);
            this.store.dispatch(action);
        } else {
            this.upsertErrorHandling(user);
        }
    }

    upsertUserData(
        callbackEmitter: EventEmitter<UserActions.UserActions | RouterGo>,
        updatedUserData: User = {} as User,
        logData: any = {}
    ) {
        const action: UserActions.UserAction = this.getUpsertAction(updatedUserData, callbackEmitter, logData);
        this.store.dispatch(action);
    }

    upsertErrorHandling(user: User = {} as User) {
        const error = new DataServiceError<User>("Invalid submission", user);
        this.store.dispatch(this.getUpsertFailureAction(error));
    }

    isUserUpsertSuccessAction(action: UserActions.UserActions | RouterGo): action is UserActions.UserSuccessAction {
        return action.type === UserActions.createSuccess.type || action.type === UserActions.updateSuccess.type;
    }

    subscribeToEmitter(emitter: EventEmitter<UserActions.UserActions | RouterGo>) {
        if (!emitter.observers.length) {
            const emitterSubscription = emitter.subscribe(action => {
                if (action.type === routerGo.type || this.isUserUpsertSuccessAction(action)) {
                    this.store.dispatch(action);

                    if (this.isUserUpsertSuccessAction(action)) {
                        this.cancelAction(action.user.entityId, true);
                    }
                    emitterSubscription.unsubscribe();
                }
            });
        } else {
            emitter.pipe(take(1)).subscribe(action => {
                this.store.dispatch(action);
            });
        }
    }

    openDeleteUserFailureDialog(id: string, disableClose = false) {
        const onCancel = new EventEmitter();
        onCancel.subscribe(event => {
            event.close();
        });
        const data = {
            title: "Unable to delete",
            message: "You cannot delete your own user",
            buttons: [
                {
                    text: "Close",
                    method: onCancel,
                },
            ],
        };
        const config = { id, data, disableClose };
        if (!this.dialog.openDialogs || !this.dialog.openDialogs.length) {
            this.dialog.open(ClareDialogComponent, config);
        }
    }

    deleteUser(user: User, dialog_id?: string) {
        this.selectAuthUser()
            .pipe(take(1))
            .subscribe(authUser => {
                if (user && authUser && authUser.id !== user.id) {
                    this.dispatchDeleteUserConfirmation(user);
                } else {
                    //this.openDeleteUserFailureDialog("no-dealer", true);
                    const id = dialog_id ? dialog_id : "no-dealer";
                    this.openDeleteUserFailureDialog(id, true);
                }
            });
    }

    /**
     * send request to db check if staff email is existed
     *
     * @param {string} email
     * @param {number} staffId
     * @returns
     */
    validateIfStaffEmailExisted$(email: string, staffId: number = null) {
        return this.userDbService.getStaffByEmail(email).pipe(
            map(users => {
                if (
                    users.length >= 1 &&
                    users[0].email &&
                    users[0].email.toLowerCase() === email.toLowerCase() &&
                    (!staffId || staffId !== users[0].id)
                ) {
                    return true;
                }
                return false;
            }),
            catchError((error: DataServiceError<{ error: string }>) => {
                if (error.error === this.PERMISSION_DENIED_ERROR) {
                    return of(true);
                }
                throw new DataServiceError(error.error);
            })
        );
    }

    validateStaffEmail(formControls: FormGroup, existedUserId: number = null) {
        let userExists: boolean;

        return formControls.controls.email.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            map(value => (typeof value === "string" ? value : value.email)),
            switchMap((value: string) =>
                this.userDbService.getStaffByEmail(value).pipe(
                    tap(users => {
                        formControls.controls.firstName.enable();
                        formControls.controls.lastName.enable();
                        if (
                            users.length >= 1 &&
                            users[0].email &&
                            users[0].email.toLowerCase() === value.toLowerCase() &&
                            (!existedUserId || existedUserId !== users[0].id)
                        ) {
                            userExists = true;
                            formControls.controls.email.setAsyncValidators(this.emailExistsControl(userExists));
                            formControls.controls.email.updateValueAndValidity();
                            return users;
                        }

                        // Note: in case user clears out formerly valid email and
                        // inputs incorrect or existing email
                        userExists = false;
                        formControls.controls.email.setAsyncValidators(this.emailExistsControl(userExists));
                        formControls.controls.email.updateValueAndValidity();
                    }),
                    catchError((error: DataServiceError<{ error: string }>) => {
                        if (error.error === this.PERMISSION_DENIED_ERROR) {
                            userExists = true;
                            formControls.controls.email.setAsyncValidators(this.emailExistsControl(userExists));
                            formControls.controls.email.updateValueAndValidity();
                        } else {
                            throw new DataServiceError(error.error);
                        }
                        return of(null);
                    })
                )
            )
        );
    }

    emailExistsControl(userExists: boolean) {
        return (control: AbstractControl) =>
            this.store.pipe(
                filter(() => !!control.value),
                map(() =>
                    userExists
                        ? {
                              emailExists: "This email is in use by a user. Enter a different email.",
                          }
                        : undefined
                )
            );
    }
    dispatchDeleteUserConfirmation(user: User) {
        const logData = {
            auditLogName: createAuditLogKey(LOG_CATEGORY.COMPANY, LOG_TYPE.STAFF, LOG_SUB_TYPE.DELETE),
            data: UserHelper.getLogString(user),
            entityId: user.entityId,
        };
        this.store.dispatch(UserActions.deleteConfirmation({ user: { ...user }, logData }));
    }

    resendInvite(user: User) {
        const emitter = new EventEmitter();
        emitter.pipe(take(1)).subscribe(result => {
            this.store.dispatch(
                UserActions.sendUserWelcomeEmail({
                    recipientEmail: result.user.email,
                    recipientFirstName: result.user.firstName,
                    recipientLastName: result.user.lastName,
                    inviteCode: result.user.inviteCode,
                })
            );
        });
        this.store.dispatch(this.getUpsertAction(user, emitter));
    }

    cancelAction(entityId: number, dispatch = false) {
        const action = this.routerDispatchers.getRouterAction([`${entityRoutePaths.root}${entityRoutePaths.edit}/${entityId}/staff`]);
        if (dispatch) {
            this.store.dispatch(action);
        }

        return action;
    }

    goToAddPage(entityId: number) {
        this.store.dispatch(
            this.routerDispatchers.getRouterAction([`${entityRoutePaths.root}${entityRoutePaths.edit}/${entityId}/staff/add`])
        );
    }

    goToEditPage(entityId: number, id?: number) {
        this.store.dispatch(
            this.routerDispatchers.getRouterAction([`${entityRoutePaths.root}${entityRoutePaths.edit}/${entityId}/staff/edit/${id}`])
        );
    }

    goToListPage(entityId: number, extras?: NavigationExtras) {
        this.store.dispatch(
            RouterActions.routerGo({
                path: [`${entityRoutePaths.root}${entityRoutePaths.edit}/${entityId}/staff`],
                extras,
            })
        );
    }

    checkForEmailExistence(email: string): Observable<User | undefined> {
        return this.userDbService.getCustomerByEmail(email);
    }

    constructor(
        private readonly routerDispatchers: RouterDispatchers,
        readonly store: Store<UserManagementState | AuthState>,
        private readonly userDbService: UserDbService,
        private readonly dialog?: MatDialog
    ) {
        super(store);
    }
}
